import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import Button from './Button.jsx';
import { X } from 'lucide-react';

const cn = (...parts) => parts.filter(Boolean).join(' ');

/**
 * Modal **panel** size: `xs` | `sm` | `md` | `lg` (matches stories / original design).
 * Aliases: `small` → `sm`, `medium` → `md`, `large` → `lg`.
 * @param {string} [value]
 * @returns {'xs'|'sm'|'md'|'lg'}
 */
const normalizePanelSize = (value) => {
  if (value === 'xs') return 'xs';
  if (value === 'sm' || value === 'small') return 'sm';
  if (value === 'md' || value === 'medium') return 'md';
  if (value === 'lg' || value === 'large') return 'lg';
  return 'md';
};

/** `actionScale` for `Button`: `sm` | `md`. Aliases: `small` → `sm`, `medium` → `md`, `xs` → `sm`. */
const normalizeActionScale = (value) => {
  if (value === 'sm' || value === 'small' || value === 'xs') return 'sm';
  return 'md';
};

/** @param {string} [value]
 * @returns {'start'|'center'|'end'} */
const normalizeActionsAlign = (value) => {
  if (value === 'center') return 'center';
  if (value === 'left' || value === 'start') return 'start';
  if (value === 'right' || value === 'end') return 'end';
  return 'end';
};

const footerJustifyClass = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
};

const zIndexClassMap = {
  0: 'z-0',
  10: 'z-10',
  20: 'z-20',
  30: 'z-30',
  40: 'z-40',
  50: 'z-50',
  1000: 'z-[1000]',
  12050: 'z-[12050]',
};

const getZIndexClass = (value) => zIndexClassMap[value] ?? zIndexClassMap[1000];

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * `size` = modal shell (card). `actionScale` = **`Modal.Button`** + close icon.
 */
const ModalContext = createContext({
  /** @type {'sm'|'md'} */
  buttonSize: 'md',
  /** @type {'start'|'center'|'end'} */
  actionsAlign: 'end',
});

export const useModalContext = () => useContext(ModalContext);

/**
 * Footer button: inherits **`actionScale`** from `Modal` (same idea as ann `Modal.Button` + `size`).
 * Outside an open `<Modal>`, defaults to `md`.
 */
const ModalButton = ({ children, ...props }) => {
  const { buttonSize } = useModalContext();
  return (
    <Button size={buttonSize} {...props}>
      {children}
    </Button>
  );
};

/**
 * @param {'xs'|'sm'|'md'|'lg'|'small'|'medium'|'large'} [props.size='md'] — Panel size
 * @param {'sm'|'md'|'small'|'medium'|'xs'} [props.actionScale='md']
 * @param {'left'|'center'|'right'|'start'|'end'} [props.actionsAlign='right']
 * @param {'left'|'center'|'right'} [props.actionAlignment] — Alias for `actionsAlign`.
 * @param {boolean} [props.enableClose=true] — Enables X button, overlay close and Escape close.
 * @param {import('react').ReactNode} [props.header] — Optional custom header slot.
 * @param {Array<{ label: import('react').ReactNode, color?: string, icon?: import('lucide-react').LucideIcon, onClick?: (event: import('react').MouseEvent) => void, variant?: string, disabled?: boolean, type?: 'button'|'submit'|'reset' }>} [props.actions]
 * @param {string} [props.panelClassName] — Tailwind classes for panel width/height/responsiveness.
 * @param {string} [props.contentClassName] — Tailwind classes for content slot.
 * @param {string} [props.actionsClassName] — Tailwind classes for generated actions row.
 * @param {string} [props.overlayClassName] — Tailwind classes for the overlay wrapper.
 * @param {string} [props.closeLabel] — Close button `aria-label` (i18n).
 * @param {string} [props.ariaLabel] — Dialog `aria-label` when there is no visible `title`.
 * @param {string} [props.portalRootId] — If an element with this `id` exists (e.g. `shell-main-content`),
 *   the overlay portals there with `absolute inset-0` so shell top bar / sidebar stay usable.
 *   Otherwise uses `document.body` with `fixed inset-0`.
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  actionScale = 'md',
  actionsAlign = 'right',
  actionAlignment,
  className = '',
  zIndex = 1000,
  enableClose = true,
  header,
  actions,
  panelClassName = '',
  contentClassName = '',
  actionsClassName = '',
  overlayClassName = '',
  closeLabel = 'Close modal',
  ariaLabel,
  portalRootId,
}) => {
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const titleId = useId();
  const previousActiveElementRef = useRef(null);

  const panelSize = normalizePanelSize(size);
  const buttonSize = normalizeActionScale(actionScale);
  const footerAlign = normalizeActionsAlign(actionAlignment ?? actionsAlign);

  const closeIconSize = { sm: 18, md: 20 };

  const hasTitle =
    title != null && String(title).trim() !== '';
  const titleText = typeof title === 'string' ? title.trim() : '';
  const canUseShellClose = enableClose && typeof onClose === 'function';

  const cardHover =
    'transition-[border-color] duration-150 hover:border-modal-border-hover';

  /** Panel: surface + modal border token (`--modal-border` in index.css). */
  const panelShellBase = [
    'border-[1.5px] border-solid border-modal-border bg-surface',
    'shadow-none',
    cardHover,
    'animate-modal-fade-in-delayed',
  ];

  const panelPreset =
    panelSize === 'xs'
      ? {
          shell: [
            'w-full max-w-[min(420px,calc(100%-2rem))]',
            'flex flex-col gap-3 rounded-[8px]',
            ...panelShellBase,
            'px-2 py-2',
          ],
          bodyGap: 'gap-3',
          title:
            'flex-1 pr-2 text-xs font-bold leading-tight text-content',
        }
      : panelSize === 'sm'
        ? {
            shell: [
              'w-full max-w-[min(720px,calc(100%-2rem))]',
              'flex flex-col gap-4 rounded-[8px]',
              ...panelShellBase,
              'px-3 py-3',
            ],
            bodyGap: 'gap-4',
            title:
              'flex-1 pr-2 text-sm font-bold leading-tight text-content',
          }
        : panelSize === 'lg'
          ? {
              shell: [
                'w-full max-w-[min(1040px,calc(100%-2rem))]',
                'flex flex-col gap-6 rounded-[8px]',
                ...panelShellBase,
                'px-5 py-6',
              ],
              bodyGap: 'gap-6',
              title:
                'flex-1 pr-2 text-lg font-bold leading-tight text-content',
            }
          : {
            shell: [
              'w-full max-w-[min(901px,calc(100%-2rem))]',
              'flex flex-col gap-6 rounded-[8px]',
              ...panelShellBase,
              'px-4 py-6',
            ],
            bodyGap: 'gap-6',
            title:
              'flex-1 pr-2 text-base font-bold leading-tight text-content',
          };

  const panelClasses = panelPreset.shell;
  const bodyGapClass = panelPreset.bodyGap;
  const titleClass = panelPreset.title;

  useEffect(() => {
    if (!isOpen) return;

    previousActiveElementRef.current = document.activeElement;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (canUseShellClose) onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const panel = panelRef.current;
      if (!panel.contains(document.activeElement)) return;

      const focusable = Array.from(
        panel.querySelectorAll(FOCUSABLE_SELECTOR)
      ).filter(
        (el) =>
          el.offsetParent !== null &&
          !el.hasAttribute('disabled') &&
          el.getAttribute('aria-hidden') !== 'true'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    const focusFrame = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const bodyEl = panel.querySelector('[data-modal-body]');
      const inBody = bodyEl
        ? Array.from(bodyEl.querySelectorAll(FOCUSABLE_SELECTOR))
        : [];
      const toFocus =
        inBody[0] ?? panel.querySelector('button[type="button"]');
      if (toFocus && typeof toFocus.focus === 'function') {
        toFocus.focus();
      }
    });

    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      const prev = previousActiveElementRef.current;
      if (prev && typeof prev.focus === 'function') {
        try {
          if (document.body.contains(prev)) prev.focus();
        } catch {
          /* ignore */
        }
      }
    };
  }, [canUseShellClose, isOpen, onClose]);

  /**
   * Portal wrapper: mirror `class="dark"` from `document.documentElement` so tokens (`bg-surface`,
   * `border-edge`) and Tailwind `dark:` variants apply while the modal is open (e.g. Storybook
   * before the preview syncs `<html>`).
   */
  const [portalRootDark, setPortalRootDark] = useState(false);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPortalRootDark(false);
      return;
    }
    const root = document.documentElement;
    const sync = () => setPortalRootDark(root.classList.contains('dark'));
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => mo.disconnect();
  }, [isOpen]);

  const handleOverlayClick = (e) => {
    if (canUseShellClose && e.target === overlayRef.current) onClose();
  };

  if (!isOpen) return null;

  const portalTarget =
    typeof document !== 'undefined' && portalRootId?.trim()
      ? document.getElementById(portalRootId.trim()) ?? document.body
      : document.body;
  const overlayInsetInShell = portalTarget !== document.body;
  const overlayLayoutClass = overlayInsetInShell
    ? 'absolute inset-0 min-h-full min-w-full'
    : 'fixed inset-0';
  const overlayZIndexClass = getZIndexClass(zIndex);
  const dialogLabel = ariaLabel ?? (titleText || 'Modal dialog');

  const dialogAria =
    hasTitle && !header
      ? { 'aria-labelledby': titleId }
      : { 'aria-label': dialogLabel };

  const hasConfiguredActions = Array.isArray(actions) && actions.length > 0;

  return createPortal(
    <div className={portalRootDark ? 'dark' : undefined}>
      <ModalContext.Provider value={{ buttonSize, actionsAlign: footerAlign }}>
        <div
          ref={overlayRef}
          onClick={handleOverlayClick}
          className={cn(
            overlayLayoutClass,
            overlayZIndexClass,
            'flex animate-modal-fade-in items-center justify-center bg-black/[0.35] p-4 backdrop-blur-md dark:bg-black/50',
            overlayClassName,
          )}
        >
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            {...dialogAria}
            className={cn(...panelClasses, className, panelClassName)}
            onClick={(e) => e.stopPropagation()}
          >
            {header ?? (
              hasTitle || canUseShellClose ? (
                <div
                  className={
                    hasTitle
                      ? 'flex items-start justify-between gap-4'
                      : 'flex items-start justify-end gap-4'
                  }
                >
                  {hasTitle ? (
                    <h2 id={titleId} className={titleClass}>
                      {title}
                    </h2>
                  ) : null}
                  {canUseShellClose ? (
                    <button
                      type="button"
                      onClick={onClose}
                      aria-label={closeLabel}
                      className="inline-flex shrink-0 cursor-pointer border-0 bg-transparent p-0 text-content-secondary hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2 dark:text-content-muted dark:hover:text-content"
                    >
                      <X size={closeIconSize[buttonSize]} strokeWidth={2} />
                    </button>
                  ) : null}
                </div>
              ) : null
            )}

            <div
              data-modal-body
              className={cn('flex min-h-0 flex-1 flex-col', bodyGapClass, contentClassName)}
            >
              {children}
              {hasConfiguredActions ? (
                <ModalFooter className={actionsClassName}>
                  {actions.map((action, index) => {
                    const Icon = action.icon ?? null;
                    return (
                      <ModalButton
                        key={action.key ?? `${index}-${action.label ?? 'action'}`}
                        type={action.type ?? 'button'}
                        variant={action.variant ?? action.color ?? 'secondary'}
                        disabled={action.disabled}
                        onClick={action.onClick}
                        {...(Icon ? { leftIcon: Icon } : {})}
                      >
                        {action.label}
                      </ModalButton>
                    );
                  })}
                </ModalFooter>
              ) : null}
            </div>
          </div>
        </div>
      </ModalContext.Provider>
    </div>,
    portalTarget
  );
};

/** Matches ann `Modal.Actions`: 16px gap. Alignment from `actionsAlign` on `<Modal>`. */
const ModalFooter = ({ children, className = '' }) => {
  const { actionsAlign = 'end' } = useModalContext();
  const justify = footerJustifyClass[actionsAlign] ?? 'justify-end';
  return (
    <div
      className={['flex flex-wrap items-center', justify, 'gap-4', className]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
};

Modal.Footer = ModalFooter;
Modal.Button = ModalButton;

export default Modal;
